import { useEffect, useState } from 'react';
import { doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { artifactsCollection } from '../../constants/firestorePaths';
import { crfReadiness, type CrfReadiness } from './crfReadiness';
import { isApprovedCrfStatus, sessionFormCountOf } from './crfApprovalRule';

/** The shared readiness answer, plus the raw count of crf artifacts of any status. */
export interface CrfApproval extends CrfReadiness {
  /** All `crf` artifacts, approved or not. `null` while loading. */
  artifactTotal: number | null;
}

/**
 * Live "are the case report forms approved" answer for a project, read from the
 * same two stores, with the same rules, as the Study Documents hub: the CRF
 * Builder's session forms and the approved `crf` artifacts. Read only.
 *
 * Both counts start `null`, so `state` is 'unknown' until each store has
 * answered, and a read failure on the session leaves it unknown rather than
 * reading as "no forms". Callers must treat 'unknown' as "do not offer Generate
 * yet" and must not tell the customer their forms are missing.
 */
export function useCrfApproval(projectId: string | undefined): CrfApproval {
  const [sessionFormCount, setSessionFormCount] = useState<number | null>(null);
  const [counts, setCounts] = useState<{ total: number; approved: number } | null>(null);

  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      doc(db, 'projects', projectId, 'crfBuilder', 'session'),
      (snap) => setSessionFormCount(sessionFormCountOf(snap.data() as { forms?: unknown } | undefined)),
      () => setSessionFormCount(null),
    );
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      query(artifactsCollection(projectId), where('type', '==', 'crf')),
      (snapshot) => {
        let approved = 0;
        snapshot.forEach((d) => {
          if (isApprovedCrfStatus(d.data().status)) approved++;
        });
        setCounts({ total: snapshot.size, approved });
      },
      // A read failure must not read as "nothing approved": leave it unknown.
      () => setCounts(null),
    );
  }, [projectId]);

  return {
    ...crfReadiness({
      sessionFormCount,
      approvedArtifactCount: counts === null ? null : counts.approved,
    }),
    artifactTotal: counts === null ? null : counts.total,
  };
}
